/* eslint-disable @typescript-eslint/naming-convention */
import { useContext, useCallback, useMemo } from "react";

// contexts
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// helpers
import {
    extractOFOAddressFromEditAddressNB,
    getIsAwayLocation,
    getOFOWidgetDetails,
    getOrderFlowType,
} from "@cart-client/helpers/Cart";
import { openEditNumberBottomSheet } from "@cart-client/helpers/OFO";

// interfaces
import { NativeLocalCart, UserAddress } from "@swiggy-private/cart-types";
import { SOCIAL_OFO_TEXT } from "@cart-client/constants/TextDict";
import { FLOW_TYPE_OFO } from "@cart-client/constants/index";

// analytics events
import {
    clickAddDetails,
    clickEditDetails,
    impressionAddDetails,
    impressionEditDetails,
} from "@cart-client/analytics/events/OFO";

export interface IOfoWidgetData {
    canShowOFOWidget?: boolean;
    title?: string;
    subTitle?: string;
    actionText?: string;
    showGiftIcon?: boolean;
    onActionClick: VoidFunction;
    onImpression: VoidFunction;
}

export const useOfoWidgetData = ({
    selectedAddress,
    localCartState,
}: {
    selectedAddress: UserAddress | null;
    localCartState: NativeLocalCart | null;
}): IOfoWidgetData => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { doUpdateCart } = useContext(UpdateCartContext);

    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse, localCart } = cartState;

    const { titleOFOWidget, subTitleOFOWidget, actionTextOFOWidget, showGiftIcon } = useMemo(
        () => getOFOWidgetDetails(selectedAddress, getOrderFlowType(cartResponse), getIsAwayLocation(cartResponse)),
        [selectedAddress, cartResponse],
    );

    const showOFOWidget = useMemo(
        () => getOrderFlowType(cartResponse) === FLOW_TYPE_OFO || getIsAwayLocation(cartResponse),
        [cartResponse],
    );

    const canShowOFOWidget = useMemo(() => showOFOWidget && !!titleOFOWidget, [showOFOWidget, titleOFOWidget]);

    const onClickOFOWidgetEvent = useCallback(() => {
        if (actionTextOFOWidget === SOCIAL_OFO_TEXT.editAction) {
            clickEditDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, clickEvent);
        } else {
            clickAddDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, false, clickEvent);
        }
    }, [actionTextOFOWidget, cartResponse, localCartState, clickEvent]);

    const onImpressionOFOWidgetEvent = useCallback(() => {
        if (actionTextOFOWidget === SOCIAL_OFO_TEXT.editAction) {
            impressionEditDetails(cartResponse?.data?.cartId || "", !!localCartState?.userLiveLat, impressionEvent);
        } else {
            impressionAddDetails(
                cartResponse?.data?.cartId || "",
                !!localCartState?.userLiveLat,
                false,
                impressionEvent,
            );
        }
    }, [actionTextOFOWidget, cartResponse, localCartState, impressionEvent]);

    const onOFOWidgetActionClick = useCallback(() => {
        onClickOFOWidgetEvent();
        selectedAddress &&
            openEditNumberBottomSheet(selectedAddress, actionTextOFOWidget).then(response => {
                const parsedAddress = extractOFOAddressFromEditAddressNB(response);
                const newAddressId = parsedAddress?.id || localCart?.address_id;
                doUpdateCart({
                    ...(localCart || {}),
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    address_id: newAddressId?.toString(),
                });
            });
    }, [actionTextOFOWidget, doUpdateCart, localCart, selectedAddress, onClickOFOWidgetEvent]);

    return {
        canShowOFOWidget,
        title: titleOFOWidget,
        subTitle: subTitleOFOWidget,
        actionText: actionTextOFOWidget,
        showGiftIcon,
        onActionClick: onOFOWidgetActionClick,
        onImpression: onImpressionOFOWidgetEvent,
    };
};
